import {
  SliderLabel,
  SliderMark,
  Slider,
  Box,
  Text,
  Heading,
  useDebounce
} from '@homebotapp/hb-react-component-catalog'
import { defineMessages, useIntl } from 'react-intl'
import useViewport from '../../../../hooks/useViewport'
import { HomeSearchTrackingEventLocation } from '../../../../constants/tracking'
import { useState } from 'react'

interface ShortTermRentalSlidersProps {
  nights: number
  price: number
  calculatedData: any
  setPrice: (price: number) => void
  setNights: (nights: number) => void
}

export const MSG = defineMessages({
  howNiceWouldYouMakeIt: {
    id: 'ShortTermRentalSliders.howNiceWouldYouMakeIt',
    defaultMessage: 'How nice would you make it?'
  },
  howManyNightsPerMonth: {
    id: 'ShortTermRentalSliders.howManyNightsPerMonth',
    defaultMessage: 'How many nights per month?'
  },
  basic: {
    id: 'ShortTermRentalSliders.basic',
    defaultMessage: 'basic'
  },
  fancy: {
    id: 'ShortTermRentalSliders.fancy',
    defaultMessage: 'fancy'
  },
  seldom: {
    id: 'ShortTermRentalSliders.seldom',
    defaultMessage: 'seldom'
  },
  often: {
    id: 'ShortTermRentalSliders.often',
    defaultMessage: 'often'
  },
  val: {
    id: 'ShortTermRentalSliders.val',
    defaultMessage: '${num}'
  }
})

export const ShortTermRentalSliders: React.FC<ShortTermRentalSlidersProps> = ({
  nights,
  price,
  calculatedData,
  setPrice,
  setNights
}) => {
  const viewport = useViewport()
  const intl = useIntl()

  const [localPrice, setLocalPrice] = useState(price)
  const [localNights, setLocalNights] = useState(nights)

  const debouncePriceUpdate = useDebounce(() => {
    setPrice(localPrice)
  }, 500)

  const debounceNightUpdate = useDebounce(() => {
    setNights(localNights)
  }, 500)

  return (
    <>
      <Heading as='h4' size='xs' mb={viewport.isSmall ? 8 : 10}>
        {intl.formatMessage(MSG.howNiceWouldYouMakeIt)}
      </Heading>
      <Box mb={viewport.isSmall ? 6 : 12} mx={4}>
        <Slider
          name='pricePerNight'
          aria-labelledby='pricePerNightLabel'
          value={localPrice}
          min={1}
          max={Math.round(calculatedData.paymentMax)}
          tracking={{
            guid: 's9SAnNFmetjBsQh4qHvcbX',
            descriptor: 'price per night',
            ui_context:
              HomeSearchTrackingEventLocation.HOME_SEARCH_LISTING_DETAILS_SHORT_TERM_RENTAL_PRICE_PER_NIGHT_SLIDER
          }}
          onChange={val => {
            setLocalPrice(val)
            debouncePriceUpdate()
          }}
        >
          <SliderMark value={1}>{intl.formatMessage(MSG.basic)}</SliderMark>
          <SliderLabel value={localPrice}>
            <Text size='2xs' as='span' whiteSpace='nowrap'>
              {intl.formatMessage(MSG.val, { num: localPrice })}
            </Text>
          </SliderLabel>
          <SliderMark value={Math.round(calculatedData.paymentMax)}>{intl.formatMessage(MSG.fancy)}</SliderMark>
        </Slider>
      </Box>
      <Heading as='h4' size='xs' mb={viewport.isSmall ? 8 : 10}>
        {intl.formatMessage(MSG.howManyNightsPerMonth)}
      </Heading>
      <Box mb={viewport.isSmall ? 6 : 12} mx={4}>
        <Slider
          name='nightsPerMonth'
          aria-label='nights per month'
          value={localNights}
          min={0}
          max={Math.round(calculatedData.rentalNightMax)}
          tracking={{
            guid: 'dvjmo9ub5PowvGUVxQBvdi',
            descriptor: 'nights per month',
            ui_context:
              HomeSearchTrackingEventLocation.HOME_SEARCH_LISTING_DETAILS_SHORT_TERM_RENTAL_NIGHTS_PER_MONTH_SLIDER
          }}
          onChange={val => {
            setLocalNights(val)
            debounceNightUpdate()
          }}
        >
          <SliderMark value={0.5}>{intl.formatMessage(MSG.seldom)}</SliderMark>
          <SliderLabel value={localNights}>
            <Text size='2xs' as='span' whiteSpace='nowrap'>
              {localNights}
            </Text>
          </SliderLabel>
          <SliderMark value={Math.round(calculatedData.rentalNightMax)}>{intl.formatMessage(MSG.often)}</SliderMark>
        </Slider>
      </Box>
    </>
  )
}
